import React from 'react'
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import { IBarangJadiRequest } from 'interfaces/IBarangJadiRequest'
import { formatTimestamp } from 'shared/dateFormatter'
import {
  BARANG_JADI_IN_OPTIONS,
  BARANG_JADI_OUT_OPTIONS,
} from 'interfaces/IBarangJadiRequest'
import { VALUE_IN } from 'enums/stockCard'

const useStyles = makeStyles(() => ({
  expandableDetails: { paddingTop: 0, paddingBottom: 0 },
  hiddenDetails: { paddingTop: 0 },
  row: { cursor: 'pointer' },
}))

function getLabel(key: string, direction: string): string {
  const options =
    direction === VALUE_IN ? BARANG_JADI_IN_OPTIONS : BARANG_JADI_OUT_OPTIONS
  const match = (options as readonly { key: string; label: string }[]).find(
    (o) => o.key === key
  )
  return match ? match.label : key
}

export default function BarangJadiRequestList() {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [requests, setRequests] = React.useState<IBarangJadiRequest[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>({})
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false)
  const [selectedRequest, setSelectedRequest] =
    React.useState<IBarangJadiRequest | null>(null)
  const [snackbar, setSnackbar] = React.useState({
    isSuccessOpen: false,
    isFailureOpen: false,
    message: '',
  })

  React.useEffect(() => {
    dispatch({
      type: 'SET_PAGE_TITLE',
      payload: 'Daftar Request Barang Jadi In/Out',
    })
  }, [dispatch])

  React.useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    setIsLoading(true)
    try {
      const response = await API.get(
        'products',
        '/products/getBarangJadiRequests',
        { queryStringParameters: { includeDetails: 'true' } }
      )
      response.sort(
        (a: IBarangJadiRequest, b: IBarangJadiRequest) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime()
      )
      setRequests(response)
    } catch {
      setSnackbar({
        isFailureOpen: true,
        isSuccessOpen: false,
        message: 'Gagal memuat daftar request.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const canCancel = (req: IBarangJadiRequest) =>
    !!req.stockCard && !(req.stockCard as any).isConfirmed

  const getCancelReason = (req: IBarangJadiRequest): string | null => {
    if (!req.stockCard) return 'Tidak ada Kartu Stok terkait.'
    if ((req.stockCard as any).isConfirmed) return 'Kartu Stok sudah dikonfirmasi.'
    return null
  }

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return
    setIsLoading(true)
    try {
      await API.post('products', '/products/cancelBarangJadiRequest', {
        body: { barangJadiRequestId: selectedRequest.barangJadiRequestId },
      })
      setSnackbar({
        isSuccessOpen: true,
        isFailureOpen: false,
        message: 'Berhasil membatalkan request.',
      })
      await loadRequests()
    } catch {
      setSnackbar({
        isSuccessOpen: false,
        isFailureOpen: true,
        message: `Gagal membatalkan ${selectedRequest.barangJadiRequestDisplayId}.`,
      })
    } finally {
      setIsLoading(false)
      setCancelDialogOpen(false)
      setSelectedRequest(null)
    }
  }

  return (
    <Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none" />
                <TableCell>Nomor Request</TableCell>
                <TableCell>Tanggal</TableCell>
                <TableCell>Nomor Kartu Stok</TableCell>
                <TableCell>In/Out</TableCell>
                <TableCell>Dari/Tujuan</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Grid container justifyContent="center" alignItems="center">
                      <SmoothLoadingIndicator size={40} />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography>Tidak ada request barang jadi.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {requests.map((req) => {
                const rowKey = String(req.barangJadiRequestId)
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      className={classes.row}
                      hover
                      onClick={() =>
                        setExpandedRows((prev) => ({
                          ...prev,
                          [rowKey]: !prev[rowKey],
                        }))
                      }
                    >
                      <TableCell padding="none">
                        <IconButton aria-label="expand row" size="small">
                          {expandedRows[rowKey] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {req.barangJadiRequestDisplayId || '-'}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp('id-ID', req.createdAt)}
                      </TableCell>
                      <TableCell>
                        {(req.stockCard as any)?.stockCardDisplayId ?? 'Pending'}
                      </TableCell>
                      <TableCell>{req.inOutDirection}</TableCell>
                      <TableCell>
                        {getLabel(req.sourceOrDestination, req.inOutDirection)}
                      </TableCell>
                      <TableCell>
                        {canCancel(req) ? (
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRequest(req)
                              setCancelDialogOpen(true)
                            }}
                          >
                            Batalkan
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {getCancelReason(req)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className={
                          expandedRows[rowKey]
                            ? classes.hiddenDetails
                            : classes.expandableDetails
                        }
                      >
                        <Collapse
                          in={expandedRows[rowKey]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box py={1}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    <Box fontWeight="bold">Produk</Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box fontWeight="bold">Nomor Bets</Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box fontWeight="bold">Qty Diminta</Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box fontWeight="bold">Qty Dikonfirmasi</Box>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {((req as any).tblBarangJadiRequestItem ??
                                  req.items ??
                                  []
                                ).map((item: any, idx: number) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      {item.productName ||
                                        `Product #${item.productId}`}
                                    </TableCell>
                                    <TableCell>{item.batchNumber}</TableCell>
                                    <TableCell>{item.qty}</TableCell>
                                    <TableCell>
                                      {(req.stockCard as any)?.stockCardProducts?.find(
                                        (p: any) => p.productId === item.productId
                                      )?.confirmedQuantity ?? 'Pending'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {req.notes && (
                              <Box mt={1} px={1}>
                                <Typography variant="body2">
                                  <Box fontWeight="bold" display="inline">
                                    Notes:{' '}
                                  </Box>
                                  {req.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cancel dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Konfirmasi Pembatalan</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Apakah anda yakin ingin membatalkan request berikut?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius="4px">
                <Typography variant="body2">
                  <strong>Nomor Request:</strong>{' '}
                  {selectedRequest.barangJadiRequestDisplayId}
                </Typography>
                <Typography variant="body2">
                  <strong>Tanggal:</strong>{' '}
                  {formatTimestamp('id-ID', selectedRequest.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Arah:</strong> {selectedRequest.inOutDirection}
                </Typography>
              </Box>
              {getCancelReason(selectedRequest) && (
                <Box mt={2}>
                  <MuiAlert severity="warning">
                    Tidak dapat dibatalkan: {getCancelReason(selectedRequest)}
                  </MuiAlert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Batal</Button>
          {selectedRequest && canCancel(selectedRequest) && (
            <Button
              onClick={handleConfirmCancel}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? 'Memproses...' : 'Konfirmasi Batalkan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.isSuccessOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, isSuccessOpen: false }))}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={snackbar.isFailureOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, isFailureOpen: false }))}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}
